import { reactive } from "vue";

// Reactive holder of the currently-selected business for the signed-in user.
// Mirrors iOS: selection persists across reloads, keyed per user email so a
// shared device behaves correctly across accounts.

const storageKey = (userEmail) => `lulu.businessContext.${userEmail}`;

const safeGetItem = (key) => {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    return window.localStorage.getItem(key);
  } catch (e) {
    return null;
  }
};

const safeSetItem = (key, value) => {
  try {
    if (typeof window === "undefined" || !window.localStorage) return;
    window.localStorage.setItem(key, value);
  } catch (e) {
    // Quota / private-mode failures are non-fatal - selection just won't persist.
  }
};

const safeRemoveItem = (key) => {
  try {
    if (typeof window === "undefined" || !window.localStorage) return;
    window.localStorage.removeItem(key);
  } catch (e) {
    // Swallow - same rationale as above.
  }
};

export const businessContext = reactive({
  selectedBusinessId: null,
  availableBusinesses: [],
  // Cached human-readable names per business id. Populated lazily by the
  // BusinessSwitcher and invalidated by the M5 branding editor whenever a
  // displayName is saved, so the app-bar label updates immediately without
  // requiring a page refresh or re-fetch.
  displayNames: {},
});

// Pure write - components that mutate the displayed business name (today
// that's only the branding page after a save) should call this so every
// reactive consumer (BusinessSwitcher, app-bar label) re-renders at once.
export const setDisplayName = (businessId, name) => {
  if (!businessId) return;
  const next = { ...businessContext.displayNames };
  if (typeof name === "string" && name.length > 0) {
    next[businessId] = name;
  } else {
    delete next[businessId];
  }
  businessContext.displayNames = next;
};

export const setSelectedBusiness = (businessId, userEmail) => {
  businessContext.selectedBusinessId = businessId;
  if (userEmail && businessId) {
    safeSetItem(storageKey(userEmail), businessId);
  }
};

export const ensureSelectedBusiness = (userEmail, employerIds) => {
  const ids = Array.isArray(employerIds) ? employerIds : [];
  businessContext.availableBusinesses = ids;

  if (ids.length === 0) {
    businessContext.selectedBusinessId = null;
    return null;
  }

  const current = businessContext.selectedBusinessId;
  if (current && ids.includes(current)) {
    return current;
  }

  const stored = userEmail ? safeGetItem(storageKey(userEmail)) : null;
  if (stored && ids.includes(stored)) {
    businessContext.selectedBusinessId = stored;
    return stored;
  }

  const fallback = ids[0];
  businessContext.selectedBusinessId = fallback;
  if (userEmail) {
    safeSetItem(storageKey(userEmail), fallback);
  }
  return fallback;
};

export const clearForUser = (userEmail) => {
  businessContext.selectedBusinessId = null;
  businessContext.availableBusinesses = [];
  businessContext.displayNames = {};
  if (userEmail) {
    safeRemoveItem(storageKey(userEmail));
  }
};
